import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useHolding } from '@/components/holding/hooks/useHolding'
import { usePortfolioAdminWithoutPortfolio } from '@/components/hooks/usePortfolioAdminWithoutPortfolio'
import { usePortfolioOnboardingAccess } from '@/components/hooks/usePortfolioOnboardingAccess'
import { useAccessPermissions } from '@/hooks/useAccessPermissions'
import { useAuth } from '@/hooks/useAuth'
import {
  isOrganizationAdminPath,
  isOrganizationHomePath,
  isOrganizationScopedPath,
  isPortfolioWorkspaceHomePath,
  isProductWorkspaceHomePath,
  organizationIdFromPath,
} from '@/lib/workspace/routerHelpers'
import { OrganizationStatus } from '@/types/organization'

export const useRouteProtection = () => {
  const { user, isLoaded: isAuthLoaded } = useAuth()
  const {
    activeOrganizationId: organizationId,
    hasAdminOrganizationMembership,
    hasOrganizationMembership,
    isOrganizationLoading,
    hasPortfolioManagerOrganizationMembership,
    adminOrganizations,
  } = useAccessPermissions()
  const { isHoldingLoading, isPortfolioUser, isProductUser } = useHolding()
  const { hasPortfolios, shouldBlockPortfolioAdminWithoutPortfolio } =
    usePortfolioAdminWithoutPortfolio({
      organizationId,
      user,
    })

  const navigate = useNavigate()
  const location = useLocation()

  const lacksAccess = !hasOrganizationMembership

  const isOnPortfolioHome = isPortfolioWorkspaceHomePath(location.pathname)
  const isOnProductHome = isProductWorkspaceHomePath(location.pathname)
  const isOnHomePage = isOrganizationHomePath(location.pathname)
  const isOnHoldingPage = location.pathname === '/holding'

  const isOnOrganizationScopedRoute = isOrganizationScopedPath(
    location.pathname
  )

  const isOnOrganizationAdminPage = isOrganizationAdminPath(location.pathname)
  const scopedOrganizationId = organizationIdFromPath(location.pathname)
  const isScopedAdminOrganizationNotActive = adminOrganizations.some(
    (organization) =>
      organization.id === scopedOrganizationId &&
      organization.status !== OrganizationStatus.Active
  )

  const hasProductHoldingAssignments = isPortfolioUser && isProductUser

  const shouldCheckHoldingAssignments =
    !!user &&
    !hasAdminOrganizationMembership &&
    !lacksAccess &&
    (isOnHomePage || isOnHoldingPage)

  const isDataLoading = !isAuthLoaded || isOrganizationLoading
  const { shouldBlockPortfolioOnboarding } = usePortfolioOnboardingAccess({
    hasPortfolioManagerOrganizationMembership,
    isEnabled: !isDataLoading && !!user,
  })

  const canEvaluateHoldingAssignments =
    shouldCheckHoldingAssignments && !isHoldingLoading

  const shouldRedirectProductHomeToHolding =
    canEvaluateHoldingAssignments &&
    isOnProductHome &&
    !hasProductHoldingAssignments

  const shouldRedirectPortfolioHomeToHolding =
    canEvaluateHoldingAssignments && isOnPortfolioHome && !isPortfolioUser

  const shouldRedirectHomeToHolding =
    !hasPortfolios &&
    (shouldRedirectProductHomeToHolding || shouldRedirectPortfolioHomeToHolding)

  const shouldRedirectAdminToOrganizations =
    hasAdminOrganizationMembership && !isOnOrganizationScopedRoute

  useEffect(() => {
    if (isDataLoading) {
      return
    }

    if (!user) {
      navigate('/', { replace: true })
      return
    }

    if (isScopedAdminOrganizationNotActive) {
      navigate('/organizations', { replace: true })
      return
    }

    if (shouldRedirectAdminToOrganizations) {
      navigate('/organizations', { replace: true })
      return
    }

    if (!hasAdminOrganizationMembership && isOnOrganizationAdminPage) {
      navigate('/holding', { replace: true })
      return
    }

    if (shouldRedirectHomeToHolding || lacksAccess) {
      navigate('/holding', { replace: true })
    }
  }, [
    user,
    isDataLoading,
    isAuthLoaded,
    hasOrganizationMembership,
    hasAdminOrganizationMembership,
    isScopedAdminOrganizationNotActive,
    location.pathname,
    isOnOrganizationScopedRoute,
    isOnOrganizationAdminPage,
    shouldRedirectAdminToOrganizations,
    shouldRedirectHomeToHolding,
    navigate,
  ])

  return {
    isDataLoading,
    shouldBlockContent:
      !user ||
      shouldRedirectAdminToOrganizations ||
      shouldBlockPortfolioAdminWithoutPortfolio ||
      shouldBlockPortfolioOnboarding ||
      isScopedAdminOrganizationNotActive ||
      (!hasAdminOrganizationMembership && isOnOrganizationAdminPage) ||
      shouldRedirectHomeToHolding ||
      lacksAccess,
  }
}
